import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import MediaUploader from '../../components/MediaUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesPageManager = () => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/sections?page=services`);
      const servicesPage = response.data.find(s => s.section_key === 'services_page');
      
      if (servicesPage) {
        setPageContent(servicesPage);
      } else {
        // Create default structure
        setPageContent({
          page: 'services',
          section_key: 'services_page',
          content: {
            hero_title: 'Our Services',
            hero_subtitle: 'Comprehensive real estate solutions tailored to your ambitions',
            hero_image: '',
            cta_title: 'Ready to start your project?',
            cta_subtitle: "Let's discuss how we can help bring your vision to life."
          }
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching page content:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!pageContent) return;

    setSaving(true);
    try {
      if (pageContent.id) {
        // Update existing
        await axios.put(`${BACKEND_URL}/api/cms/sections/${pageContent.id}`, {
          content: pageContent.content
        });
      } else {
        // Create new
        await axios.post(`${BACKEND_URL}/api/cms/sections`, {
          page: 'services',
          section_key: 'services_page',
          content: pageContent.content
        });
      }

      toast({
        title: "Success",
        description: "Services page updated successfully"
      });

      fetchPageContent();
    } catch (error) {
      console.error('Error updating page content:', error);
      toast({
        title: "Error",
        description: "Failed to update services page",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (field, value) => {
    setPageContent({
      ...pageContent,
      content: {
        ...pageContent.content,
        [field]: value
      }
    });
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Services Page Management</h1>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Hero Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">Hero Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Hero Title</label>
            <Input
              value={pageContent.content?.hero_title || ''}
              onChange={(e) => updateContent('hero_title', e.target.value)}
              placeholder="Our Services"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Hero Subtitle</label>
            <Textarea
              value={pageContent.content?.hero_subtitle || ''}
              onChange={(e) => updateContent('hero_subtitle', e.target.value)}
              placeholder="Comprehensive real estate solutions..."
              rows={2}
            />
          </div>
          <MediaUploader
            label="Hero Background Image (optional)"
            currentUrl={pageContent.content?.hero_image || ''}
            onMediaSelect={(url) => updateContent('hero_image', url)}
            accept="image/*"
          />
          <p className="text-xs text-[#2d2d2d]">
            💡 Leave empty to use the default gradient background
          </p>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-light text-[#2d2d2d] mb-4">Call-to-Action Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">CTA Title</label>
            <Input
              value={pageContent.content?.cta_title || ''}
              onChange={(e) => updateContent('cta_title', e.target.value)}
              placeholder="Ready to start your project?"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">CTA Subtitle</label>
            <Textarea
              value={pageContent.content?.cta_subtitle || ''}
              onChange={(e) => updateContent('cta_subtitle', e.target.value)}
              placeholder="Let's discuss how we can help..."
              rows={2}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPageManager;
